import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import { Flex } from '$components/layouts';
import { getExpressDeliveryWithCoords } from '$services/same-day-delivery.service';
import { InputField } from '$components/elements/input-field';
import { useTranslation } from '$hooks';
import { SvgIcon } from '$components/elements/svg-icon';
import { Text } from '$components/elements/text';
import { theme } from '$theme';
import { Button } from '$components/elements/button';
import { ExpressDelivery } from './components'; //ExpressDelivery
import { IExpresseDeliveryResponse } from '$models';
import { useTempBasket } from '~/store/temp-basket';
import { B2CDeliveryMethod, checkoutPageUrl } from '$constants'; //B2CDeliveryMethod
import { Loading } from '$components/elements/loading';
import { getGeoLocation } from '$services/geolocator.service';

type Props = {
    itemNumber?: string;
    productPrice?: string;
    selectedZipCode?: string;
    sameDayDeliveryData?: IExpresseDeliveryResponse | null;
    validatingSameDayDelivery?: boolean;
    defaultDeliveryText?: string;
    deliveryMethod?: number;
    onZipCodeSearch?: (zipCode: string) => void;
};

type FormData = {
    search: string;
};

export const ProductDeliveryOptions = ({
    itemNumber,
    productPrice,
    selectedZipCode,
    sameDayDeliveryData,
    validatingSameDayDelivery,
    // defaultDeliveryText,
    deliveryMethod,
    onZipCodeSearch,
}: Props) => {
    const { push } = useRouter();
    const [initial, setInitial] = useState(true);
    const [isCreatingTempBasket, setIsCreatingTempBasket] = useState(false);
    const [geoSameDayData, setGeoSameDayData] = useState<IExpresseDeliveryResponse | null>();
    const { translate } = useTranslation();
    const [loadingGeoSearch, setLoadingGeoSearch] = useState(false);
    const { createExpressBasket } = useTempBasket();
    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm<FormData>();

    useEffect(() => {
        if (selectedZipCode) {
            setValue('search', selectedZipCode, { shouldDirty: true, shouldTouch: true });
        }
    }, [selectedZipCode]);

    useEffect(() => {
        if (!validatingSameDayDelivery && sameDayDeliveryData !== undefined) {
            setInitial(false);
        }
    }, [validatingSameDayDelivery]);

    const onSubmit = handleSubmit((data) => {
        if (!data.search) {
            return;
        }
        onZipCodeSearch?.(data.search);
    });

    const onExpressDelivery = async () => {
        if (!itemNumber) {
            return;
        }
        setIsCreatingTempBasket(true);
        // TODO get correct zip code from geo search
        const zipCode = geoSameDayData ? geoSameDayData?.storeInformation?.address?.zip : selectedZipCode;
        const store = geoSameDayData?.storeInformation ?? sameDayDeliveryData?.storeInformation;
        if (!zipCode) {
            setIsCreatingTempBasket(false);
            return;
        }
        const success = await createExpressBasket(itemNumber, selectedZipCode, store);
        setIsCreatingTempBasket(false);

        if (success) {
            push(checkoutPageUrl);
        }
    };

    const onGeoSeach = () => {
        setLoadingGeoSearch(true);
        getGeoLocation(
            async ({ coords }) => {
                const data = await getExpressDeliveryWithCoords(
                    itemNumber,
                    coords.latitude?.toString(),
                    coords.longitude?.toString()
                );
                setGeoSameDayData(data);
                setInitial(false);
            },
            () => setLoadingGeoSearch(false)
        );
    };

    const shouldHideExpressDelivery = useMemo(
        () => deliveryMethod === B2CDeliveryMethod.Download,
        [deliveryMethod, sameDayDeliveryData]
    );

    const noSelectionExtraText = translate('productPage.delivery.optionsNoSelectionExtraText');

    return (
        <Container column>
            <Form onSubmit={onSubmit}>
                <InputContainer gap="medium">
                    <SearchInputField
                        defaultValue={selectedZipCode}
                        label={translate('productPage.delivery.searchZip')}
                        error={errors.search}
                        maxLength={4}
                        {...register('search', {
                            required: translate('validation.error.fieldRequired'),
                            pattern: { value: /[0-9]{4}/, message: translate('validation.error.zipcodeInvalid') },
                        })}
                    />
                </InputContainer>
                <Button width="content" type="submit">
                    {translate('general.search')}
                </Button>
            </Form>
            <MainContent>
                {validatingSameDayDelivery && (
                    <NoSelectionContainer column alignItems="center" gap="medium">
                        <Loading />
                    </NoSelectionContainer>
                )}
                {!validatingSameDayDelivery && initial && (
                    <NoSelectionContainer column alignItems="center" gap="medium">
                        <NoSelectionText center color={theme.colors.textSubtle}>
                            {translate('productPage.delivery.optionsNoSelectionText')}
                        </NoSelectionText>
                        {!!noSelectionExtraText && (
                            <NoSelectionText center color={theme.colors.textSubtle}>
                                {noSelectionExtraText}
                            </NoSelectionText>
                        )}
                        <Button onClick={onGeoSeach} loading={loadingGeoSearch}>
                            <ButtonIcon color={theme.colors.white} size={24} svg="pin2" />
                            {translate('productPage.delivery.geoButtonText')}
                        </Button>
                    </NoSelectionContainer>
                )}
                {!validatingSameDayDelivery && !initial && !shouldHideExpressDelivery && (
                    <ItemList>
                        <ExpressDelivery
                            isLoading={isCreatingTempBasket}
                            price={productPrice}
                            data={geoSameDayData || sameDayDeliveryData}
                            onClick={onExpressDelivery}
                        />
                    </ItemList>
                )}
            </MainContent>
        </Container>
    );
};

const Container = styled(Flex)(({ theme }) => ({
    padding: `0 ${theme.space[3]}`,
    marginBottom: theme.space[8],
}));

const Form = styled.form(({ theme }) => ({
    marginTop: theme.space[2],
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.space[4],
    marginBottom: theme.space[4],
}));

const InputContainer = styled(Flex)({
    position: 'relative',
    flex: 1,
});

const SearchInputField = styled(InputField)({
    flex: 1,
});

const MainContent = styled(Flex)({
    flexDirection: 'column',
    height: '100%',
});

const NoSelectionContainer = styled(Flex)({
    marginTop: '30%',
});

const ButtonIcon = styled(SvgIcon)(({ theme }) => ({
    marginRight: theme.space[3],
}));

const NoSelectionText = styled(Text)({
    maxWidth: '270px',
});

const ItemList = styled.ul(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.space[4],
    border: theme.general.border,
    borderRadius: theme.general.borderRadius,

    '& li': {
        borderBottom: theme.general.border,
    },
}));
